import type { ProviderDto } from '@af/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Ext } from '@/components/ui/guide';
import { Field, Input, Select } from '@/components/ui/input';
import { ErrorBox } from '@/components/ui/misc';
import { call } from '@/lib/api';
import { relTime } from '@/lib/format';
import { notifyError, qk, useAgents, useProviders } from '@/lib/queries';

/**
 * The signed-in person's own Claude account for a provider (docs/claude-login-sandbox.md). Claude Code
 * signs in itself through Anthropic's page in a private container; this dialog only shows Anthropic's URL
 * and hands the code back to the waiting CLI. No credential ever reaches the browser or the platform.
 */
export function ClaudeLoginDialog({ provider, onClose }: { provider: ProviderDto; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ['claude-login', provider.id];
  const q = useQuery({
    queryKey: key,
    queryFn: () => call('getClaudeLogin', { params: { id: provider.id } }),
    // Poll while Claude Code is starting or waiting for the code.
    refetchInterval: (query) =>
      ['pending', 'awaiting_code'].includes(query.state.data?.login?.status ?? '') ? 2000 : false,
  });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const login = q.data?.login ?? null;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };
  const start = () =>
    run(async () => {
      setSent(false);
      setCode('');
      await call('startClaudeLogin', { params: { id: provider.id }, body: {} });
    });
  const submit = () =>
    run(async () => {
      await call('submitClaudeLoginCode', { params: { id: provider.id }, body: { code: code.trim() } });
      setSent(true);
      setCode('');
    });
  const disconnect = () => {
    if (!confirm(t('claudeLogin.disconnectConfirm'))) return;
    void run(() => call('disconnectClaudeLogin', { params: { id: provider.id } }));
  };

  const status = login?.status;
  // The provider as it is now (the list refreshes after a change of how it pays).
  const current = useProviders().data?.find((p) => p.id === provider.id) ?? provider;
  const usesLogin = current.authMode === 'sandbox_login' || current.authMode === 'hybrid_login';
  const switchToLogin = () =>
    run(async () => {
      await call('updateProvider', {
        params: { id: provider.id },
        body: { authMode: current.apiKeyHint ? 'hybrid_login' : 'sandbox_login' },
      });
      await qc.invalidateQueries({ queryKey: qk.providers });
    });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('claudeLogin.title')}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">{t('claudeLogin.intro')}</p>
          <p className="text-xs text-muted">{t('claudeLogin.who')}</p>
          <ErrorBox error={q.error} />
          {q.isPending ? (
            <Spinner />
          ) : !q.data?.available ? (
            <p className="text-sm">{t('claudeLogin.unavailable')}</p>
          ) : status === 'pending' ? (
            <p className="flex items-center gap-2 text-sm">
              <Spinner /> {t('claudeLogin.starting')}
            </p>
          ) : status === 'awaiting_code' && login?.loginUrl ? (
            <div className="flex flex-col gap-3">
              <Ext href={login.loginUrl}>{t('claudeLogin.open')}</Ext>
              <p className="text-sm">{t('claudeLogin.paste')}</p>
              <form
                className="flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (code.trim()) void submit();
                }}
                autoComplete="off"
              >
                <Field label={t('claudeLogin.codeLabel')} htmlFor="cl-code">
                  <Input
                    id="cl-code"
                    value={code}
                    spellCheck={false}
                    autoComplete="one-time-code"
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Field>
                <Button type="submit" variant="primary" loading={busy || sent} disabled={!code.trim()}>
                  {sent ? t('claudeLogin.sent') : t('claudeLogin.send')}
                </Button>
              </form>
            </div>
          ) : status === 'connected' ? (
            <div className="flex flex-col gap-1 text-sm">
              <span className="text-success">
                ✓{' '}
                {t('claudeLogin.connected', {
                  who: login?.accountEmailHint ? ` · ${login.accountEmailHint}` : '',
                })}
              </span>
              <span className="text-xs text-muted">
                {[
                  login?.planHint ? t('claudeLogin.plan', { plan: login.planHint }) : null,
                  login?.lastOkAt ? t('claudeLogin.lastOk', { when: relTime(login.lastOkAt) }) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {!usesLogin ? (
                <div className="mt-2 flex flex-col items-start gap-1.5 rounded-md border border-warn/40 bg-warn/5 p-2.5">
                  <span className="text-xs text-fg-2">{t('claudeLogin.notUsedYet')}</span>
                  <Button size="sm" variant="primary" loading={busy} onClick={() => void switchToLogin()}>
                    {t('claudeLogin.useForAgents')}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : status === 'expired' ? (
            <p className="text-sm text-danger">{t('claudeLogin.expired')}</p>
          ) : status === 'error' ? (
            <p className="text-sm text-danger">
              {t('claudeLogin.failed', { code: login?.lastErrorCode ?? 'error' })}
            </p>
          ) : null}
          {q.data?.available && status !== 'pending' && status !== 'awaiting_code' ? (
            <Button
              variant={status === 'connected' ? 'outline' : 'primary'}
              loading={busy}
              onClick={() => void start()}
            >
              {login ? t('claudeLogin.restart') : t('claudeLogin.start')}
            </Button>
          ) : null}
          {login ? (
            <Button variant="ghost" className="text-danger" loading={busy} onClick={disconnect}>
              {t('claudeLogin.disconnect')}
            </Button>
          ) : null}
          {q.data?.available ? (
            <AgentsOnAccount
              mine={q.data.agents}
              busy={busy}
              onSet={(id, mine) =>
                void run(() => call('setAgentLoginOwner', { params: { id }, body: { mine } }))
              }
            />
          ) : null}
          <p className="text-2xs text-muted">{t('providers.claudeLoginTerms')}</p>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Which agents run on this account; another agent can be put on it (only by yourself, for yourself). */
function AgentsOnAccount({
  mine,
  busy,
  onSet,
}: {
  mine: { id: string; name: string; chosen: boolean }[];
  busy: boolean;
  onSet: (agentId: string, mine: boolean) => void;
}) {
  const { t } = useTranslation();
  const all = useAgents();
  const [pick, setPick] = useState('');
  const mineIds = new Set(mine.map((a) => a.id));
  const others = (all.data ?? []).filter((a) => !mineIds.has(a.id) && a.status !== 'archived');
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      <span className="text-xs font-medium text-fg-2">{t('claudeLogin.agentsTitle')}</span>
      {mine.length ? (
        <ul className="flex flex-col gap-1 text-sm">
          {mine.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2">
              <span>
                {a.name}
                {a.chosen ? (
                  <span className="ml-1.5 text-2xs text-muted">{t('claudeLogin.chosen')}</span>
                ) : null}
              </span>
              {a.chosen ? (
                <Button size="sm" variant="ghost" loading={busy} onClick={() => onSet(a.id, false)}>
                  {t('claudeLogin.removeAgent')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted">{t('claudeLogin.agentsNone')}</p>
      )}
      {others.length ? (
        <div className="flex items-end gap-2">
          <Select
            aria-label={t('claudeLogin.addAgent')}
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="flex-1"
          >
            <option value="">{t('claudeLogin.addAgent')}</option>
            {others.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant="outline"
            disabled={!pick}
            loading={busy}
            onClick={() => {
              onSet(pick, true);
              setPick('');
            }}
          >
            {t('claudeLogin.add')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
